// Configuración de la app de WhatsApp: Modo de respuesta · Bienvenida · Perfil
// del negocio · Catálogo · Horario. Todo con datos reales; lo que aún no existe
// se dice (no se simula). Números y conexión, y Plantillas, tienen ahora su
// propia pestaña (NumbersView / DifusionView).

import { useEffect, useState } from 'react';
import {
  CalendarClock, Clock, Loader2, MessageCircle, Package, ShieldCheck, Store,
} from 'lucide-react';

import {
  getCatalog, getHours, getProfile, MODE_HINT, MODE_LABEL, numberLabel,
  saveProfile, setReplyMode,
  type ReplyMode, type WaCatalogItem, type WaNumberStatus, type WaOverview, type WaProfile,
} from '../api';
import { MODE_ICON, prettyPhone } from './shared';
import { Card, H, SectionLayout, useSection, type Section } from './Sections';
import { WhatsAppWelcomeSettings } from './WelcomeSettings';

type Notify = (type: 'success' | 'error' | 'info', title: string, message?: string) => void;
type Tab = 'modo' | 'bienvenida' | 'perfil' | 'catalogo' | 'horario';

const TABS: Section<Tab>[] = [
  { key: 'modo', label: 'Modo de respuesta', Icon: ShieldCheck },
  { key: 'bienvenida', label: 'Bienvenida', Icon: MessageCircle },
  { key: 'perfil', label: 'Perfil del negocio', Icon: Store },
  { key: 'catalogo', label: 'Catálogo', Icon: Package },
  { key: 'horario', label: 'Horario', Icon: CalendarClock },
];

export function SettingsView({ overview, onOverview, statuses, notify, isMobile }: {
  overview: WaOverview | null;
  onOverview: (o: WaOverview) => void;
  statuses: Record<string, WaNumberStatus | undefined>;
  notify: Notify;
  isMobile: boolean;
}) {
  const [tab, setTab] = useSection(TABS);
  return (
    <SectionLayout label="Configuración de WhatsApp" sections={TABS} current={tab} onSelect={setTab} isMobile={isMobile}>
      {tab === 'modo' && <ModeTab overview={overview} onOverview={onOverview} notify={notify} statuses={statuses} />}
      {tab === 'bienvenida' && (
        <>
          <H title="Bienvenida" sub="El primer mensaje que recibe alguien que te escribe por WhatsApp." />
          <WhatsAppWelcomeSettings notify={notify} canEdit={overview ? !!overview.can_edit : true} />
        </>
      )}
      {tab === 'perfil' && <ProfileTab canEdit={!!overview?.can_edit} notify={notify} />}
      {tab === 'catalogo' && <CatalogTab />}
      {tab === 'horario' && <HoursTab />}
    </SectionLayout>
  );
}


/* ── Modo de respuesta ───────────────────────────────────────────── */

function ModeTab({ overview, onOverview, notify, statuses }: {
  overview: WaOverview | null;
  onOverview: (o: WaOverview) => void;
  notify: Notify;
  statuses: Record<string, WaNumberStatus | undefined>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const can = !!overview?.can_edit;
  const apply = async (mode: ReplyMode | null, key?: string) => {
    setBusy(key || 'org');
    try {
      const o = await setReplyMode(mode, key);
      onOverview({ ...o, can_edit: true });
      notify('success', 'Modo de respuesta actualizado', mode ? MODE_LABEL[mode] : 'Igual que la organización');
    } catch (e: any) {
      notify('error', 'No se cambió el modo', e?.message);
    } finally {
      setBusy(null);
    }
  };
  const org = overview?.org_mode ?? 'auto';
  return (
    <>
      <H title="Modo de respuesta" sub="Decide si el agente contesta solo, si solo te sugiere la respuesta o si atiende tu equipo." />
      {!can && overview && <p className="mb-3 rounded-xl border border-border bg-muted px-3 py-2 text-[13px] text-muted-foreground">Solo administración puede cambiar el modo. Puedes verlo y tomar el control de cualquier conversación.</p>}
      <div role="radiogroup" aria-label="Modo de la organización" className="grid gap-3 sm:grid-cols-3">
        {(['auto', 'suggest', 'off'] as ReplyMode[]).map((m) => {
          const Icon = MODE_ICON[m];
          const sel = org === m;
          return (
            <button key={m} type="button" role="radio" aria-checked={sel} disabled={!can || !!busy}
              onClick={() => !sel && apply(m)}
              className={[
                'rounded-2xl border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]',
                sel ? 'border-[hsl(var(--wa-accent))] bg-card ring-1 ring-[hsl(var(--wa-accent))]' : 'border-border bg-card hover:bg-muted/50',
                !can ? 'cursor-not-allowed' : '',
              ].join(' ')}>
              <span className="flex items-center gap-2 text-[15px] font-semibold">
                <Icon className={`h-5 w-5 ${sel ? 'wa-accent-text' : 'text-muted-foreground'}`} aria-hidden /> {MODE_LABEL[m]}
                {busy === 'org' && sel && <Loader2 className="ml-auto h-4 w-4 animate-spin" aria-hidden />}
              </span>
              <span className="mt-1.5 block text-[13px] text-muted-foreground">{MODE_HINT[m]}</span>
            </button>
          );
        })}
      </div>

      {(overview?.numbers.length || 0) > 0 && (
        <Card className="mt-5">
          <h3 className="text-[14px] font-semibold">Por número</h3>
          <p className="mt-0.5 text-[12.5px] text-muted-foreground">Un número puede tener su propio modo; si no, usa el de la organización.</p>
          <ul className="mt-3 divide-y divide-border">
            {overview!.numbers.map((n) => (
              <li key={n.key} className="flex flex-wrap items-center gap-3 py-2.5">
                <span className="min-w-0 flex-1 text-[14px] tabular-nums">{prettyPhone(numberLabel(n, statuses[n.key]))}
                  <span className="ml-2 text-[12px] text-muted-foreground">{n.provider === 'meta' ? 'Cloud API' : 'Twilio'}</span></span>
                <label className="sr-only" htmlFor={`nm-${n.key}`}>Modo de {numberLabel(n)}</label>
                <select id={`nm-${n.key}`} value={n.reply_mode ?? 'inherit'} disabled={!can || !!busy}
                  onChange={(e) => apply(e.target.value === 'inherit' ? null : (e.target.value as ReplyMode), n.key)}
                  className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]">
                  <option value="inherit">Igual que la organización ({MODE_LABEL[org]})</option>
                  <option value="auto">Automático</option>
                  <option value="suggest">Solo sugiere</option>
                  <option value="off">Apagado</option>
                </select>
                {busy === n.key && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="mt-5 space-y-2 text-[13px]">
        <h3 className="text-[14px] font-semibold">Cómo se decide</h3>
        <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
          <li><strong className="text-foreground">Tomar control</strong> en una conversación siempre gana: el agente deja de enviar y te sugiere.</li>
          <li>El modo que elijas <strong className="text-foreground">en esa conversación</strong> (encabezado del chat).</li>
          <li>El modo del <strong className="text-foreground">número</strong> por el que escribió el cliente.</li>
          <li>El modo de la <strong className="text-foreground">organización</strong> (arriba). Sin elegir nada: Automático.</li>
        </ol>
        <p className="text-muted-foreground">
          En <strong className="text-foreground">Solo sugiere</strong> y <strong className="text-foreground">Apagado</strong> tampoco salen solos los
          seguimientos automáticos a leads ni el remarketing por WhatsApp: quedan registrados como no enviados. En Apagado el agente no lee
          ni procesa los mensajes (no se transcriben las notas de voz).
        </p>
        {overview?.last_change && (
          <p className="text-[12px] text-muted-foreground">Último cambio: {new Date(overview.last_change.at).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })} · {overview.last_change.scope === 'org' ? 'organización' : prettyPhone(overview.last_change.scope.split(':')[1])}</p>
        )}
      </Card>
    </>
  );
}

/* ── Perfil ──────────────────────────────────────────────────────── */

const VERTICAL_LABEL: Record<string, string> = {
  UNDEFINED: 'Sin categoría', OTHER: 'Otro', AUTO: 'Automotriz', BEAUTY: 'Belleza y spa', APPAREL: 'Ropa',
  EDU: 'Educación', ENTERTAIN: 'Entretenimiento', EVENT_PLAN: 'Eventos', FINANCE: 'Finanzas', GROCERY: 'Supermercado',
  GOVT: 'Gobierno', HOTEL: 'Hotel y hospedaje', HEALTH: 'Salud', NONPROFIT: 'Sin fines de lucro',
  PROF_SERVICES: 'Servicios profesionales', RETAIL: 'Comercio', TRAVEL: 'Viajes y turismo', RESTAURANT: 'Restaurante',
  NOT_A_BIZ: 'No es negocio',
};

function ProfileTab({ canEdit, notify }: { canEdit: boolean; notify: Notify }) {
  const [state, setState] = useState<{ loading: boolean; available?: boolean; motivo?: string; key?: string | null }>({ loading: true });
  const [p, setP] = useState<WaProfile>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    getProfile()
      .then((r) => { setState({ loading: false, available: r.available, motivo: r.motivo, key: r.number_key }); setP(r.profile || {}); })
      .catch((e) => setState({ loading: false, available: false, motivo: e?.message }));
  }, []);
  const set = (k: keyof WaProfile, v: any) => { setP((x) => ({ ...x, [k]: v })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const r = await saveProfile(state.key ?? null, {
        about: p.about ?? '', description: p.description ?? '', address: p.address ?? '', email: p.email ?? '',
        websites: (p.websites || []).filter(Boolean), vertical: p.vertical || undefined,
      });
      setP(r.profile || p);
      setDirty(false);
      notify('success', 'Perfil actualizado en WhatsApp');
    } catch (e: any) {
      notify('error', 'No se guardó el perfil', e?.message);
    } finally {
      setSaving(false);
    }
  };
  const field = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-70';
  const sites = [...(p.websites || []), '', ''].slice(0, 2);
  return (
    <>
      <H title="Perfil del negocio" sub="Lo que tus clientes ven al abrir la información de tu cuenta en WhatsApp." />
      {state.loading && <div className="h-64 animate-pulse rounded-2xl bg-muted" aria-hidden />}
      {!state.loading && !state.available && <Card><p className="text-[14px]">{state.motivo || 'El perfil no está disponible para este número.'}</p></Card>}
      {!state.loading && state.available && (
        <Card>
          <div className="flex items-center gap-4">
            {p.profile_picture_url
              ? <img src={p.profile_picture_url} alt="Foto de perfil del negocio" className="h-16 w-16 rounded-full object-cover" />
              : <span className="grid h-16 w-16 place-items-center rounded-full bg-muted"><Store className="h-7 w-7 text-muted-foreground" aria-hidden /></span>}
            <p className="text-[12.5px] text-muted-foreground">La foto se cambia desde WhatsApp Manager de Meta.</p>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="sm:col-span-2 block text-[12.5px] font-medium">Info (estado)
              <input className={field} maxLength={139} disabled={!canEdit} value={p.about || ''} onChange={(e) => set('about', e.target.value)} />
            </label>
            <label className="sm:col-span-2 block text-[12.5px] font-medium">Descripción
              <textarea className={field} rows={3} maxLength={512} disabled={!canEdit} value={p.description || ''} onChange={(e) => set('description', e.target.value)} />
            </label>
            <label className="block text-[12.5px] font-medium">Categoría
              <select className={field} disabled={!canEdit} value={p.vertical || 'UNDEFINED'} onChange={(e) => set('vertical', e.target.value)}>
                {Object.entries(VERTICAL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="block text-[12.5px] font-medium">Correo
              <input className={field} type="email" maxLength={128} disabled={!canEdit} value={p.email || ''} onChange={(e) => set('email', e.target.value)} />
            </label>
            <label className="sm:col-span-2 block text-[12.5px] font-medium">Dirección
              <input className={field} maxLength={256} disabled={!canEdit} value={p.address || ''} onChange={(e) => set('address', e.target.value)} />
            </label>
            {sites.map((s, i) => (
              <label key={i} className="block text-[12.5px] font-medium">Sitio web {i + 1}
                <input className={field} type="url" placeholder="https://" disabled={!canEdit} value={s}
                  onChange={(e) => { const w = [...sites]; w[i] = e.target.value; set('websites', w.filter((x, j) => x || j < i)); }} />
              </label>
            ))}
          </div>
          {canEdit ? (
            <div className="mt-4 flex justify-end">
              <button type="button" onClick={save} disabled={!dirty || saving}
                className="wa-btn inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13.5px] font-semibold">
                {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Guardar en WhatsApp
              </button>
            </div>
          ) : <p className="mt-3 text-[12.5px] text-muted-foreground">Solo administración puede editar el perfil.</p>}
        </Card>
      )}
    </>
  );
}

/* ── Catálogo ────────────────────────────────────────────────────── */

function CatalogTab() {
  const [data, setData] = useState<{ available: boolean; motivo?: string; catalog?: { id: string; name: string } | null; items?: WaCatalogItem[]; more?: boolean; error?: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { getCatalog().then(setData).catch((e) => setErr(e?.message || 'Error')); }, []);
  const avail: Record<string, string> = { 'in stock': 'Disponible', 'out of stock': 'Agotado', 'available for order': 'Bajo pedido' };
  return (
    <>
      <H title="Catálogo" sub="Los productos o experiencias que tus clientes ven en tu perfil de WhatsApp." />
      {!data && !err && <div className="h-48 animate-pulse rounded-2xl bg-muted" aria-hidden />}
      {err && <Card><p className="text-[14px] text-destructive">{err}</p></Card>}
      {data && !data.available && <Card><p className="text-[14px]">{data.motivo}</p></Card>}
      {data?.available && !data.catalog && <Card><p className="text-[14px]">{data.error ? `Meta no devolvió el catálogo: ${data.error}` : 'Tu número no tiene un catálogo conectado.'}</p></Card>}
      {data?.catalog && (
        <>
          <p className="mb-3 text-[13px] text-muted-foreground"><strong className="text-foreground">{data.catalog.name}</strong> · {data.items?.length || 0} artículos{data.more ? ' (se muestran los primeros 100)' : ''}</p>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {(data.items || []).map((it) => (
              <li key={it.id} className="overflow-hidden rounded-2xl border border-border bg-card">
                {it.image_url
                  ? <img src={it.image_url} alt="" loading="lazy" className="aspect-square w-full object-cover" />
                  : <span className="grid aspect-square place-items-center bg-muted"><Package className="h-8 w-8 text-muted-foreground" aria-hidden /></span>}
                <div className="p-2.5">
                  <p className="line-clamp-2 text-[13px] font-medium">{it.name}</p>
                  <p className="mt-0.5 text-[13px] tabular-nums">{it.price}</p>
                  <p className="mt-1 text-[11.5px] text-muted-foreground">{avail[String(it.availability)] || it.availability}{it.review_status && it.review_status !== 'approved' ? ` · Revisión: ${it.review_status}` : ''}</p>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[12.5px] text-muted-foreground">Por ahora el catálogo se consulta aquí; para agregar o cambiar artículos usa Commerce Manager de Meta.</p>
        </>
      )}
    </>
  );
}

/* ── Horario ─────────────────────────────────────────────────────── */

const DAYS = ['', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function HoursTab() {
  const [h, setH] = useState<{ tz: string; start: string; end: string; days: number[]; uso: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { getHours().then(setH).catch((e) => setErr(e?.message || 'Error')); }, []);
  return (
    <>
      <H title="Horario" sub="El horario de atención de tu organización." />
      {!h && !err && <div className="h-32 animate-pulse rounded-2xl bg-muted" aria-hidden />}
      {err && <Card><p className="text-[14px] text-destructive">{err}</p></Card>}
      {h && (
        <Card>
          <div className="flex flex-wrap gap-1.5" aria-label="Días de atención">
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <span key={d} className={`grid h-9 w-11 place-items-center rounded-lg text-[13px] font-medium ${h.days.includes(d) ? 'wa-accent-soft wa-accent-text' : 'bg-muted text-muted-foreground line-through'}`}>{DAYS[d]}</span>
            ))}
          </div>
          <p className="mt-3 flex items-center gap-2 text-[15px] font-semibold tabular-nums"><Clock className="h-4 w-4" aria-hidden /> {h.start} – {h.end}
            <span className="text-[12.5px] font-normal text-muted-foreground">({h.tz.replace('America/', '').replace('_', ' ')})</span></p>
          <p className="mt-2 text-[13px] text-muted-foreground">{h.uso}</p>
        </Card>
      )}
      <Card className="mt-4">
        <h3 className="text-[14px] font-semibold">Mensaje fuera de horario</h3>
        <p className="mt-1 text-[13px] text-muted-foreground">Todavía no existe: hoy el agente atiende a cualquier hora según el modo elegido. Está anotado como pendiente.</p>
      </Card>
    </>
  );
}
